import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Form, Spin } from "antd";
import {
  CheckOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  ProfileOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axios from "axios";

import {
  selectUserEditErrors,
  selectUserFromUserState,
} from "../../../redux-toolkit/user/user-selector";
import FormInput from "../../../components/FormInput/FormInput";
import IconButton from "../../../components/IconButton/IconButton";
import { updateUserInfo } from "../../../redux-toolkit/user/user-thunks";
import { resetInputForm } from "../../../redux-toolkit/user/user-slice";
import { BASE_URL } from "../../../constants/urlConstants";

import "./PersonalData.css";

interface PersonalDataFields {
  firstName: string;
  lastName: string;
  city: string;
  address: string;
  phoneNumber: string;
  postIndex: string;
}

const VIEW_FIELDS: { key: string; label: string }[] = [
  { key: "email",         label: "Email" },
  { key: "firstName",     label: "First Name" },
  { key: "lastName",      label: "Last Name" },
  { key: "city",          label: "City" },
  { key: "address",       label: "Address" },
  { key: "phoneNumber",   label: "Phone Number" },
  { key: "postIndex",     label: "Post Index" },
  { key: "roles",         label: "Role" },
  { key: "provider",      label: "Provider" },
  { key: "active",        label: "Status" },
  { key: "distributorId", label: "Distributor ID" },
  { key: "meEmpId",       label: "ME EmpId" },
];

/** Returns a safe display string. Reads distId from localStorage for distributorId field. */
const na = (val: any, key?: string): string => {
  if (val === null || val === undefined || String(val).trim() === "") return "—";
  if (key === "active") return val === true || val === "true" ? "Active" : "Inactive";
  if (Array.isArray(val)) return val.join(", ");
  return String(val);
};

/** Reads distId from localStorage, guarding against "null" / "undefined" strings. */
const getStoredDistId = (): string | null => {
  const val = localStorage.getItem("distId");
  return val && val !== "null" && val !== "undefined" ? val : null;
};

const PersonalData: FC = (): ReactElement => {
  const dispatch  = useDispatch();
  const [form]    = Form.useForm();
  const params    = useParams<{ id?: string }>();

  const loggedInUser = useSelector(selectUserFromUserState);
  const errors       = useSelector(selectUserEditErrors);
  const [editing, setEditing]   = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading]   = useState(false);

  const { firstNameError, lastNameError } = errors;

  const urlId          = params.id ? Number(params.id) : null;
  const isViewingOther = urlId !== null && urlId !== loggedInUser?.id;
  const activeUser     = isViewingOther ? userData : loggedInUser;

  useEffect(() => {
    dispatch(resetInputForm());

    if (isViewingOther && urlId) {
      setLoading(true);
      axios
        .get(`${BASE_URL}/api/v1/admin/user/${urlId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
          },
        })
        .then((res) => {
          setUserData(res.data);
          form.setFieldsValue({
            ...res.data,
            distributorId: getStoredDistId() ?? res.data.distributorId,
          });
        })
        .catch(console.error)
        .finally(() => setLoading(false));
  } else if (loggedInUser) {
    const storedDistId = getStoredDistId();
    const storedDistName = localStorage.getItem("distName");
    const displayDistributor = storedDistId && storedDistName
      ? `${storedDistId}  -  ${storedDistName}`
      : storedDistId ?? (loggedInUser as any).distributorId;

    form.setFieldsValue({
      ...loggedInUser,
      distributorId: displayDistributor,
    });
  }
  }, [dispatch, urlId, loggedInUser, form]);

  const onFormSubmit = (data: PersonalDataFields) => {
    const id = isViewingOther ? urlId : loggedInUser?.id;
    dispatch(updateUserInfo({ id, ...data }));
    setEditing(false);
  };

  const getInitial = () => {
    const name = activeUser?.firstName || activeUser?.email || "U";
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <div className="pd-page">
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="pd-page">

      {/* HEADER */}
      <div className="pd-header">
        <ProfileOutlined className="pd-header-icon" />
        <span className="pd-header-title">
          {isViewingOther ? "User Profile" : "My Account"}
        </span>
        <button
          className={`pd-edit-btn ${editing ? "active" : ""}`}
          onClick={() => setEditing((p) => !p)}
        >
          {editing ? <><EyeInvisibleOutlined /> Cancel</> : <><EditOutlined /> Edit</>}
        </button>
      </div>

      <div className="pd-content">

        {/* PROFILE CARD */}
        <div className="pd-profile-card">
          <div className="pd-avatar">{getInitial()}</div>
          <div className="pd-profile-info">
            <div className="pd-profile-name">
              {[activeUser?.firstName, activeUser?.lastName].filter(Boolean).join(" ") || "—"}
            </div>
            <div className="pd-profile-email">{activeUser?.email || "—"}</div>
          </div>
          {isViewingOther && (
            <span className="pd-viewing-badge">Admin View</span>
          )}
        </div>

        {/* DATA SECTION */}
        {!editing ? (
          <div className="pd-data-card">
            <div className="pd-section-title">
              <UserOutlined /> Personal Details
            </div>
            <div className="pd-fields">
              {VIEW_FIELDS.map(({ key, label }) => {
                const val = (activeUser as any)?.[key];

                if (
                  (val === null || val === undefined) &&
                  key !== "distributorId" &&
                  key !== "adminId"
                )
                  return null;

                return (
                  <div key={key} className="pd-field-row">
                    <span className="pd-field-label">{label}</span>
                    <span
                      className={`pd-field-value ${
                        key === "active"
                          ? val ? "pd-active" : "pd-inactive"
                          : ""
                      }`}
                    >
                      {/* ✅ Show distId (business code) from localStorage for both view & edit */}
                      {key === "distributorId"
                        ? (() => {
                            const id = getStoredDistId();
                            const name = localStorage.getItem("distName");
                            return id && name ? `${id}  -  ${name}` : id || na(val, key);
                          })()
                        : na(val, key)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="pd-data-card">
            <div className="pd-section-title">
              <EditOutlined /> Edit Details
            </div>
            <Form form={form} layout="vertical" onFinish={onFormSubmit} className="pd-form">
              <FormInput title="First name"    titleSpan={6} wrapperSpan={18} name="firstName"   error={firstNameError} placeholder="First name" />
              <FormInput title="Last name"     titleSpan={6} wrapperSpan={18} name="lastName"    error={lastNameError}  placeholder="Last name" />
              <FormInput title="City"          titleSpan={6} wrapperSpan={18} name="city"        placeholder="City" />
              <FormInput title="Address"       titleSpan={6} wrapperSpan={18} name="address"     placeholder="Address" />
              <FormInput title="Phone number"  titleSpan={6} wrapperSpan={18} name="phoneNumber" placeholder="Phone number" />
              <FormInput title="Post index"    titleSpan={6} wrapperSpan={18} name="postIndex"   placeholder="Post index" />
              <div className="pd-save-row">
                <IconButton title="Save Changes" icon={<CheckOutlined />} />
              </div>
            </Form>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalData;